import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { BookOpenCheck, Check, ChevronDown, ChevronUp, Download, Eye, Loader2, Pencil, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useMasterData } from '../contexts/MasterDataContext';
import { API } from '../utils/apiConfig';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const WEEK_OPTIONS = [
  { value: 'all', label: 'All Weeks' },
  { value: '1', label: 'Week 1' },
  { value: '2', label: 'Week 2' },
  { value: '3', label: 'Week 3' },
  { value: '4', label: 'Week 4' },
  { value: '5', label: 'Week 5' },
  { value: '6', label: 'Week 6' },
];

const AMOUNT_COLS = [
  { key: 'km', label: 'KM+Hudle' },
  { key: 'district', label: 'District' },
  { key: 'hudle', label: 'Events' },
  { key: 'cash', label: 'ETC' },
  { key: 'pos', label: 'Offline' },
  { key: 'other', label: 'Rental' },
];

const formatRowDate = (iso) => {
  try {
    // Format e.g. "19 Jul - Sun" (no year, short day suffix).
    return format(new Date(iso), 'dd MMM - EEE');
  } catch {
    return iso;
  }
};

const computeTotal = (row) =>
  AMOUNT_COLS.reduce((sum, c) => sum + (parseFloat(row[c.key]) || 0), 0);

const inr = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN')}`;

export default function DayBookEntryPage() {
  const { token, isStaff } = useAuth();
  const { getVenueNames } = useMasterData();
  const today = useMemo(() => new Date(), []);

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [week, setWeek] = useState('all');
  const [venue, setVenue] = useState('all');
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rowStatus, setRowStatus] = useState({}); // key -> 'saving' | 'saved' | 'error'
  const [exporting, setExporting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false); // collapsible on mobile
  const rowsRef = useRef([]);
  const stickyTopRef = useRef(null);
  const lastSavedRef = useRef({}); // key -> {km, district, ...} snapshot of last persisted values
  const [topBarHeight, setTopBarHeight] = useState(0);

  // Keep latest rows snapshot in a ref so debounced callbacks read fresh values
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Measure the sticky top bar so the table header can pin just below it
  useEffect(() => {
    if (!stickyTopRef.current || typeof ResizeObserver === 'undefined') return;
    const el = stickyTopRef.current;
    const update = () => setTopBarHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const venueOptions = useMemo(() => getVenueNames(), [getVenueNames]);

  const yearOptions = useMemo(() => {
    const y = today.getFullYear();
    return [y - 1, y, y + 1];
  }, [today]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { year, month, week };
      if (venue && venue !== 'all') params.venue = venue;
      const res = await axios.get(`${API}/day-book`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data.rows || []);
      // Staff view: hide entries older than 2 days ago (i.e. keep only the
      // last 3 days — today, yesterday, day-before-yesterday).
      if (isStaff) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 2);
        cutoff.setHours(0, 0, 0, 0);
        const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const cutoffIso = iso(cutoff);
        const filtered = (res.data.rows || []).filter((r) => (r.date || '') >= cutoffIso);
        setRows(filtered);
      }
      setGrandTotal(res.data.grand_total || null);
      // Snapshot saved values so we can detect changes on blur
      const snap = {};
      (res.data.rows || []).forEach((r) => {
        snap[`${r.date}__${r.venue}`] = {
          km: parseFloat(r.km) || 0,
          district: parseFloat(r.district) || 0,
          hudle: parseFloat(r.hudle) || 0,
          cash: parseFloat(r.cash) || 0,
          pos: parseFloat(r.pos) || 0,
          other: parseFloat(r.other) || 0,
        };
      });
      lastSavedRef.current = snap;
    } catch (err) {
      console.error('Day Book fetch error', err);
      toast.error('Failed to load Day Book entries');
    } finally {
      setLoading(false);
    }
  }, [year, month, week, venue, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rowKey = (r) => `${r.date}__${r.venue}`;

  const persistRow = useCallback(
    async (key) => {
      if (!token) return;
      const row = rowsRef.current.find((r) => rowKey(r) === key);
      if (!row) return;
      setRowStatus((prev) => ({ ...prev, [key]: 'saving' }));
      try {
        const payload = {
          date: row.date,
          venue: row.venue,
          km: parseFloat(row.km) || 0,
          district: parseFloat(row.district) || 0,
          hudle: parseFloat(row.hudle) || 0,
          cash: parseFloat(row.cash) || 0,
          pos: parseFloat(row.pos) || 0,
          other: parseFloat(row.other) || 0,
        };
        const res = await axios.put(`${API}/day-book`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRows((prev) =>
          prev.map((r) =>
            rowKey(r) === key
              ? {
                  ...r,
                  ...res.data,
                  last_edited_by: res.data.last_edited_by,
                  last_edited_at: res.data.last_edited_at,
                }
              : r,
          ),
        );
        setRowStatus((prev) => ({ ...prev, [key]: 'saved' }));
        // Record the saved snapshot so subsequent blurs without changes skip the save
        lastSavedRef.current[key] = {
          km: payload.km,
          district: payload.district,
          hudle: payload.hudle,
          cash: payload.cash,
          pos: payload.pos,
          other: payload.other,
        };
        // Auto-clear the 'saved' indicator after 2s
        setTimeout(() => {
          setRowStatus((prev) => {
            if (prev[key] !== 'saved') return prev;
            const next = { ...prev };
            delete next[key];
            return next;
          });
        }, 2000);
      } catch (err) {
        console.error('Auto-save error', err);
        setRowStatus((prev) => ({ ...prev, [key]: 'error' }));
        toast.error(err?.response?.data?.detail || 'Auto-save failed');
      }
    },
    [token],
  );

  // Update local state only on each keystroke — does NOT trigger a save
  const handleAmountChange = (key, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (rowKey(r) !== key) return r;
        const next = { ...r, [field]: value === '' ? 0 : Number(value) };
        next.total = computeTotal(next);
        return next;
      }),
    );
  };

  // Trigger a save ONLY on blur AND only when the value actually changed
  const handleAmountBlur = (key, field) => {
    const row = rowsRef.current.find((r) => rowKey(r) === key);
    if (!row) return;
    const currentValue = parseFloat(row[field]) || 0;
    const lastSaved = lastSavedRef.current[key]?.[field] ?? 0;
    if (currentValue === lastSaved) return; // no change — skip save
    persistRow(key);
  };

  const handleExport = async () => {
    if (!token) return;
    setExporting(true);
    try {
      const params = { year, month, week };
      if (venue && venue !== 'all') params.venue = venue;
      const res = await axios.get(`${API}/day-book/export`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const parts = [`day_book_${year}_${String(month).padStart(2, '0')}`];
      if (venue && venue !== 'all') parts.push(venue.replace(/\s+/g, '_'));
      if (week !== 'all') parts.push(`w${week}`);
      link.setAttribute('download', `${parts.join('_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Day Book exported');
    } catch (err) {
      console.error('Export error', err);
      toast.error('Failed to export Day Book');
    } finally {
      setExporting(false);
    }
  };

  // Compute client-side grand total from current row state (in-flight edits visible immediately)
  const liveGrandTotal = useMemo(() => {
    const out = { total: 0 };
    AMOUNT_COLS.forEach((c) => {
      out[c.key] = rows.reduce((s, r) => s + (parseFloat(r[c.key]) || 0), 0);
    });
    out.total = AMOUNT_COLS.reduce((s, c) => s + out[c.key], 0);
    return out;
  }, [rows]);

  const displayedGrandTotal = (grandTotal && !isStaff) ? liveGrandTotal : null;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="day-book-page">
        {/* Sticky header + filters — stays pinned while scrolling rows.
            -mt-8 negates the parent DashboardLayout py-8 so the bar pins flush to top. */}
        <div
          ref={stickyTopRef}
          className="sticky top-0 z-40 bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 -mt-8 px-4 sm:px-6 lg:px-8 pt-4 pb-3 border-b border-slate-200 shadow-sm space-y-3"
          data-testid="day-book-sticky-top"
        >
          {/* Header */}
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight flex items-center gap-2">
                <BookOpenCheck className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
                Day Book Entry
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {editMode
                  ? 'Edit Mode — saves trigger when you leave a field.'
                  : 'View Mode — click "Edit Mode" to start entering values.'}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => setEditMode((v) => !v)}
                variant={editMode ? 'default' : 'outline'}
                className={editMode ? 'bg-primary hover:bg-primary-hover' : ''}
                data-testid="day-book-edit-mode-toggle"
              >
                {editMode ? (
                  <>
                    <Eye className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">View Mode</span>
                  </>
                ) : (
                  <>
                    <Pencil className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Edit Mode</span>
                  </>
                )}
              </Button>
              {/* Mobile-only filter toggle — hidden for staff (filters not shown) */}
              {!isStaff && (
                <Button
                  onClick={() => setFiltersOpen((v) => !v)}
                  variant="outline"
                  className="sm:hidden"
                  data-testid="day-book-filters-toggle"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  {filtersOpen ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                </Button>
              )}
              {!isStaff && (
                <Button
                  onClick={handleExport}
                  disabled={exporting || loading || rows.length === 0}
                  variant="outline"
                  data-testid="day-book-export-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 sm:mr-2" />
                  )}
                  <span className="hidden sm:inline">Export to Excel</span>
                </Button>
              )}
              <Button
                onClick={fetchData}
                disabled={loading}
                variant="outline"
                data-testid="day-book-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>

          {/* Filters — collapsible on mobile via filtersOpen state, always shown on sm+.
              Staff role has no access to the top filters at all. */}
          {!isStaff && (
          <Card className={`p-4 ${filtersOpen ? 'block' : 'hidden sm:block'}`} data-testid="day-book-filters-card">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Month</label>
                <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
                  <SelectTrigger data-testid="day-book-month-select">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTH_OPTIONS.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Year</label>
                <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                  <SelectTrigger data-testid="day-book-year-select">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Week</label>
                <Select value={week} onValueChange={setWeek}>
                  <SelectTrigger data-testid="day-book-week-select">
                    <SelectValue placeholder="All Weeks" />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEK_OPTIONS.map((w) => (
                      <SelectItem key={w.value} value={w.value}>
                        {w.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Venue</label>
                <Select value={venue} onValueChange={setVenue}>
                  <SelectTrigger data-testid="day-book-venue-select">
                    <SelectValue placeholder="All Venues" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Venues</SelectItem>
                    {venueOptions.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
          )}
        </div>

        {/* Table */}
        <Card className="overflow-visible">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading entries…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="day-book-empty">
              No active venues found for this month. Add a venue under{' '}
              <span className="font-semibold">Venue &amp; Court Master</span> to begin.
            </div>
          ) : (
            <div className="w-full">
              <table className="w-full text-sm" data-testid="day-book-table">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th
                      className="px-3 py-3 text-left font-semibold sticky left-0 z-30 bg-slate-900 min-w-[110px]"
                      style={{ top: topBarHeight }}
                    >
                      Date
                    </th>
                    <th
                      className="px-3 py-3 text-left font-semibold sticky z-20 bg-slate-900 min-w-[160px]"
                      style={{ top: topBarHeight }}
                    >
                      Venue
                    </th>
                    {AMOUNT_COLS.map((c) => (
                      <th
                        key={c.key}
                        className="px-3 py-3 text-right font-semibold sticky z-20 bg-slate-900 min-w-[100px]"
                        style={{ top: topBarHeight }}
                      >
                        {c.label}
                      </th>
                    ))}
                    <th
                      className="px-3 py-3 text-right font-semibold sticky z-20 bg-slate-800 min-w-[110px]"
                      style={{ top: topBarHeight }}
                    >
                      Total
                    </th>
                    <th
                      className={`px-3 py-3 text-center font-semibold sticky z-20 bg-slate-900 min-w-[80px] ${editMode ? '' : 'hidden'}`}
                      style={{ top: topBarHeight }}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const key = rowKey(row);
                    const status = rowStatus[key];
                    return (
                      <tr
                        key={key}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`day-book-row-${row.date}-${row.venue}`}
                      >
                        <td className="px-3 py-2 text-slate-700 font-medium whitespace-nowrap sticky left-0 z-10 bg-white">
                          {formatRowDate(row.date)}
                          {row.last_edited_by && (
                            <div
                              className="text-[10px] text-slate-400 font-normal mt-0.5"
                              title={`Last edited by ${row.last_edited_by} at ${row.last_edited_at}`}
                            >
                              by {row.last_edited_by.split('@')[0]}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{row.venue}</td>
                        {AMOUNT_COLS.map((c) => (
                          <td key={c.key} className="px-2 py-1.5">
                            {editMode ? (
                              <Input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="0.01"
                                value={row[c.key] === 0 ? '' : row[c.key]}
                                onChange={(e) => handleAmountChange(key, c.key, e.target.value)}
                                onBlur={() => handleAmountBlur(key, c.key)}
                                className="h-8 text-right text-sm"
                                data-testid={`day-book-input-${row.date}-${row.venue}-${c.key}`}
                                placeholder="0"
                              />
                            ) : (
                              <div
                                className="h-8 px-2 flex items-center justify-end text-sm text-slate-700"
                                data-testid={`day-book-display-${row.date}-${row.venue}-${c.key}`}
                              >
                                {parseFloat(row[c.key]) > 0
                                  ? (parseFloat(row[c.key]) || 0).toLocaleString('en-IN')
                                  : <span className="text-slate-300">—</span>}
                              </div>
                            )}
                          </td>
                        ))}
                        <td
                          className="px-3 py-2 text-right font-semibold text-slate-900 whitespace-nowrap bg-slate-50"
                          data-testid={`day-book-row-total-${row.date}-${row.venue}`}
                        >
                          {inr(row.total)}
                        </td>
                        <td
                          className={`px-3 py-2 text-center ${editMode ? '' : 'hidden'}`}
                          data-testid={`day-book-status-${row.date}-${row.venue}`}
                        >
                          {status === 'saving' && (
                            <span className="inline-flex items-center text-xs text-slate-500">
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              Saving
                            </span>
                          )}
                          {status === 'saved' && (
                            <span className="inline-flex items-center text-xs text-emerald-600">
                              <Check className="w-3.5 h-3.5 mr-1" />
                              Saved
                            </span>
                          )}
                          {status === 'error' && (
                            <span className="text-xs text-rose-600 font-medium">Retry</span>
                          )}
                          {!status && row.last_edited_at && (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Grand total row */}
                {displayedGrandTotal && (
                  <tfoot>
                    <tr
                      className="bg-slate-900 text-white font-bold"
                      data-testid="day-book-grand-total-row"
                    >
                      <td
                        className="px-3 py-3 text-left sticky left-0 bg-slate-900"
                        colSpan={2}
                      >
                        Grand Total
                      </td>
                      {AMOUNT_COLS.map((c) => (
                        <td
                          key={c.key}
                          className="px-3 py-3 text-right"
                          data-testid={`day-book-grand-${c.key}`}
                        >
                          {inr(displayedGrandTotal[c.key])}
                        </td>
                      ))}
                      <td
                        className="px-3 py-3 text-right bg-slate-800 text-emerald-300"
                        data-testid="day-book-grand-total"
                      >
                        {inr(displayedGrandTotal.total)}
                      </td>
                      <td className={`px-3 py-3 ${editMode ? '' : 'hidden'}`} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </Card>

        <p className="text-xs text-slate-400 px-2">
          • One row per (date, venue). In <span className="font-semibold">Edit Mode</span>, type a value then click out of the field (Tab / next cell) — it auto-saves only when the value changes. Daily Total (row) and Grand Total (column) update automatically.
        </p>
      </div>
    </DashboardLayout>
  );
}
